import React, { useState, useEffect } from 'react';

function BalanceTable({ acctNum, accountList }) {
  const [accountsBalances, setAccountsBalances] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBalances = async () => {
      let listToUse = accountList;
      
      // Handle non-array props early to avoid type errors
      if (!Array.isArray(listToUse)) {
        if (acctNum && !isNaN(parseInt(acctNum, 10))) {
          listToUse = [{ accountName: 'Campaign Account', accountNum: parseInt(acctNum, 10) }];
        } else {
          console.warn('No valid accountList or acctNum provided');
          setAccountsBalances([]);
          setLoading(false);
          return;
        }
      }
      
      // Filter out invalid accounts (null, undefined, or non-numeric accountNum)
      listToUse = listToUse.filter(acc => {
        if (!acc || typeof acc.accountNum === 'undefined' || acc.accountNum === null) {
          console.warn('Skipping account with null/undefined accountNum:', acc);
          return false;
        }
        const parsed = parseInt(acc.accountNum, 10);
        if (isNaN(parsed) || parsed <= 0) {
          console.warn('Skipping invalid accountNum:', acc.accountNum, '(parsed to', parsed, ')');
          return false;
        }
        return true;
      });
      
      if (listToUse.length === 0) {
        console.warn('No valid accounts to fetch balances for');
        setAccountsBalances([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const acctNums = listToUse.map(acc => String(acc.accountNum).trim());
        console.log('Fetching bulk balances for accounts:', acctNums);
        const response = await fetch(`/api/balances?accounts=${acctNums.join(',')}`);
        if (!response.ok) {
          console.error('Bulk balances fetch failed:', response.status);
          setAccountsBalances([]);
          setLoading(false);
          return;
        }

        const balancesArray = await response.json();
        console.log('Bulk balances response:', balancesArray);

        // Normalize balancesArray into a lookup by account (tolerant of account field names)
        const balancesLookup = new Map(balancesArray.map(b => {
          const acct = b.account ?? b.AcctNum ?? b.Account ?? b.accountNum;
          return [String(acct).trim(), b];
        }));

        const results = listToUse.map(acc => {
          const accountNum = String(acc.accountNum).trim();
          const accountName = acc.accountName || 'Unknown';
          const balances = balancesLookup.get(accountNum) || {};
          return { accountNum: parseInt(accountNum, 10), accountName, balances };
        });

        setAccountsBalances(results);
      } catch (err) {
        console.error('Error fetching balances:', err);
        setError(err.message);
        setAccountsBalances([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBalances();
  }, [acctNum, accountList]);

  // Define fields in exact order, with renames
  const orderedFields = [
    { key: 'monetary', label: 'Contributions' },
    { key: 'loans', label: 'Loans' },
    { key: 'inkind', label: 'In-Kind' },
    { key: 'expend', label: 'Expenditures' },
    { key: 'transfers', label: 'Transfers' },
    { key: 'other', label: 'Other' },
    { key: 'coh', label: 'Cash on Hand' }
  ];

  // Format function for amounts
  const formatAmount = (value) => {
    const num = typeof value === 'number' ? value : 0;
    return num.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2 
    });
  };

  if (loading) return <div>Loading balance information...</div>;
  if (error) return <div>Error loading balances: {error}</div>;
  if (!accountsBalances || accountsBalances.length === 0) return <div>No balances available for this candidate.</div>;

  return (
    <div style={{ marginTop: '30px' }}>
      <h3>Account Balances</h3>
      <table border="1" style={{ borderCollapse: 'collapse', width: '100%', border: '1px solid black' }}>
        <thead>
          <tr style={{ backgroundColor: '#f2f2f2', borderTop: '1px solid black' }}>
            <th style={{ padding: '8px', textAlign: 'left', width: '20%', border: '1px solid black' }}>Account</th>
            {orderedFields.map(({ label }) => (
              <th key={label} style={{ padding: '8px', textAlign: 'left', border: '1px solid black' }}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {accountsBalances.map(({ accountName, balances }, index) => (
            <tr key={index} style={{ borderBottom: '1px solid #ddd' }}>
              <td style={{ padding: '8px', border: '1px solid black' }}>
                <strong>{accountName}</strong>
              </td>
              {orderedFields.map(({ key, label }) => (
                <td key={label} style={{ padding: '8px', border: '1px solid black' }}>
                  {formatAmount(balances[key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default BalanceTable;